import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';

const serviceLinks = [
  { name: 'Vejtransport', path: '/godstransport-i-europa', desc: 'Stykgods, partigods og full loads i Europa' },
  { name: 'Multimodal transport', path: '/fragt-kombineret-transport', desc: 'Grøn kombineret transport med vej, bane og sø' },
  { name: 'Express', path: '/ekspres-kurer-i-direkte-lastbiler', desc: 'Dedikerede biler og kurérkørsel dør-til-dør' },
  { name: 'Fragtkontoret', path: '/fragtkontoret', desc: 'International spedition, rådgivning og outsourcing' },
  { name: 'Lager & Logistik', path: '/lager', desc: '1800 m² tørt og rent lagerhotel i Esbjerg' },
  { name: 'Søfragt', path: '/containerfragt-soefragt', desc: 'LCL og FCL containerfragt i hele verden' },
  { name: 'Cross Trade', path: '/cross-trade', desc: 'Transport mellem tredjelande uden om Danmark' },
  { name: 'Fragtopgaver-referencer', path: '/godstransport-referencer', desc: 'Se billeder og eksempler på gods vi har transporteret' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const dropdownRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();

  // Scroll listener with hysteresis to guarantee no shaking/jumping between sizes
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY;
          // Hysteresis window: activate fixed/scrolled state at > 60px,
          // only return to top state when scrolled back up near the top (< 15px).
          // This eliminates edge oscillating or jitter at the boundary.
          if (currentY > 60) {
            setIsScrolled(true);
          } else if (currentY < 15) {
            setIsScrolled(false);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on page navigation
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  // Dropdown hover handlers with timeout and hover-bridge
  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setServicesDropdownOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    dropdownTimeoutRef.current = setTimeout(() => {
      setServicesDropdownOpen(false);
    }, 180);
  };

  // Click outside to close desktop dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  // Prevent background scrolling when mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const isServiceActive = serviceLinks.some((link) => location.pathname === link.path);

  return (
    <>
      <header
      className={`sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/80 transition-shadow duration-300 ease-in-out ${
        isScrolled ? 'shadow-md' : 'shadow-xs'
      }`}
    >
      {/* Top bar with contact info */}
      <div className="hidden md:block bg-[#0f2b48] text-slate-200 text-xs py-2 px-4 sm:px-6 lg:px-8 border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-y-1 gap-x-4">
          <div className="flex items-center space-x-6 flex-wrap">
            <span className="inline-flex items-center text-slate-300">
              <i className="fa-solid fa-location-dot text-sky-400 mr-1.5" aria-hidden="true" />
              <span>Storstrømsvej 42, 6715 Esbjerg N</span>
            </span>
            <a
              href="tel:+4576121230"
              className="inline-flex items-center text-slate-200 hover:text-white transition-colors whitespace-nowrap group"
              title="Ring til Fastlane ApS"
            >
              <i className="fa-solid fa-phone text-sky-400 mr-1.5 group-hover:scale-110 transition-transform" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium">+45 76 12 12 30</span>
            </a>
            <a
              href="mailto:booking@fastlane.dk"
              className="inline-flex items-center text-slate-200 hover:text-white transition-colors whitespace-nowrap group"
              title="Send e-mail til Fastlane ApS"
            >
              <i className="fa-solid fa-envelope text-sky-400 mr-1.5 group-hover:scale-110 transition-transform" aria-hidden="true" />
              <span className="whitespace-nowrap">booking@fastlane.dk</span>
            </a>
          </div>
          <div className="flex items-center space-x-4 ml-auto">
            <span className="text-slate-400 hidden md:inline">CVR: DK 26 63 78 13</span>
            <Link
              to="/fragtrater"
              className="inline-flex items-center text-amber-400 hover:text-amber-300 font-semibold transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-calculator mr-1" aria-hidden="true" />
              <span>Få et fragttilbud</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between transition-all duration-300 ease-in-out ${
            isScrolled ? 'h-16' : 'h-20'
          }`}
        >
          {/* Brand Logo - smoothly transitions to slightly smaller when scrolled */}
          <Link
            to="/"
            className="flex items-center gap-3 shrink-0 py-1.5 group focus:outline-hidden"
            aria-label="Fastlane ApS Forside"
          >
            <img
              src="/_assets/logo.png"
              alt="Fastlane ApS"
              className={`w-auto object-contain transition-all duration-300 ease-in-out group-hover:scale-[1.02] ${
                isScrolled ? 'h-8 sm:h-9' : 'h-10 sm:h-12'
              }`}
              width="180"
              height="48"
            />
          </Link>

          {/* Desktop Navigation (visible at >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center space-x-1.5" aria-label="Hovednavigation">
            {/* Fastlane Exact 7 Menu Tabs */}
            <NavLink
              to="/kvalitet"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs uppercase tracking-normal transition-colors rounded-none ${
                  isActive
                    ? 'bg-[#34588a] text-white font-semibold shadow-[2px_2px_0px_0px_#aeb2b7]'
                    : 'text-[#75797f] hover:text-[#34588a] font-normal'
                }`
              }
            >
              Kvalitet
            </NavLink>

            <NavLink
              to="/forsikring"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs uppercase tracking-normal transition-colors rounded-none ${
                  isActive
                    ? 'bg-[#34588a] text-white font-semibold shadow-[2px_2px_0px_0px_#aeb2b7]'
                    : 'text-[#75797f] hover:text-[#34588a] font-normal'
                }`
              }
            >
              Forsikring
            </NavLink>

            <NavLink
              to="/spoergsmaal-svar-fragt"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs uppercase tracking-normal transition-colors rounded-none ${
                  isActive
                    ? 'bg-[#34588a] text-white font-semibold shadow-[2px_2px_0px_0px_#aeb2b7]'
                    : 'text-[#75797f] hover:text-[#34588a] font-normal'
                }`
              }
            >
              Ofte stillede spørgsmål
            </NavLink>

            <NavLink
              to="/kendskabdownloads"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs uppercase tracking-normal transition-colors rounded-none ${
                  isActive
                    ? 'bg-[#34588a] text-white font-semibold shadow-[2px_2px_0px_0px_#aeb2b7]'
                    : 'text-[#75797f] hover:text-[#34588a] font-normal'
                }`
              }
            >
              Kendskab/Downloads
            </NavLink>

            <NavLink
              to="/nyheder"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs uppercase tracking-normal transition-colors rounded-none ${
                  isActive
                    ? 'bg-[#34588a] text-white font-semibold shadow-[2px_2px_0px_0px_#aeb2b7]'
                    : 'text-[#75797f] hover:text-[#34588a] font-normal'
                }`
              }
            >
              Nyheder
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs uppercase tracking-normal transition-colors rounded-none ${
                  isActive
                    ? 'bg-[#34588a] text-white font-semibold shadow-[2px_2px_0px_0px_#aeb2b7]'
                    : 'text-[#75797f] hover:text-[#34588a] font-normal'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs uppercase tracking-normal transition-colors rounded-none ${
                  isActive
                    ? 'bg-[#34588a] text-white font-semibold shadow-[2px_2px_0px_0px_#aeb2b7]'
                    : 'text-[#75797f] hover:text-[#34588a] font-normal'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Right Action: CTA Button */}
          {/* Mobile menu trigger - ONLY shown on screens < 1200px */}
          <div className="flex items-center min-[1200px]:hidden space-x-2">
            <Link
              to="/fragtrater"
              className="hidden md:inline-flex items-center px-3 py-1.5 rounded-md bg-[#b25d19] hover:bg-[#974d12] text-white text-xs font-semibold"
            >
              <span>Tilbud</span>
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2.5 rounded-lg text-slate-700 hover:text-slate-900 hover:bg-slate-100 focus:outline-hidden focus:ring-2 focus:ring-sky-600 transition-colors cursor-pointer"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-lg' : 'fa-bars text-lg'}`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      </header>

      {/* Mobile Drawer (Sidebar) - independently scrollable with full access to all items */}
      {mobileMenuOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="min-[1200px]:hidden fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex justify-end animate-in fade-in duration-200">
            {/* Backdrop overlay */}
            <div
              className="fixed inset-0"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Drawer sidebar panel */}
            <div className="relative w-full max-w-sm sm:max-w-md bg-white h-full max-h-screen overflow-y-auto overscroll-contain overflow-x-hidden flex flex-col shadow-2xl border-l border-slate-200 z-10">
              {/* Header */}
              <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between shrink-0 sticky top-0 z-10">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#c52828]" />
                  <span className="font-bold text-[#0f2b48] text-sm uppercase tracking-wider">Fastlane Menu</span>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded-md transition-colors cursor-pointer"
                  aria-label="Luk menu"
                >
                  <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
                </button>
              </div>

              {/* Scrollable Nav list */}
              <div className="p-4 space-y-1.5 flex-1">
                <NavLink
                  to="/"
                  end
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3.5 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                      isActive ? 'bg-[#0f2b48] text-white shadow-xs' : 'text-slate-800 hover:bg-slate-100'
                    }`
                  }
                >
                  Forside
                </NavLink>

                {/* Mobile Accordion for Services */}
                <div className="rounded-xl overflow-hidden border border-slate-200/90 my-2 shadow-2xs">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMobileServicesOpen((prev) => !prev);
                    }}
                    className={`w-full flex items-center justify-between px-3.5 py-3 text-base font-semibold transition-colors cursor-pointer ${
                      isServiceActive
                        ? 'bg-sky-50 text-[#0f2b48]'
                        : 'text-slate-800 bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <i className="fa-solid fa-truck-fast text-sky-700 text-sm" aria-hidden="true" />
                      <span>Ydelser & Services</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-slate-200/80 text-slate-700 font-normal px-2 py-0.5 rounded-full">
                        {serviceLinks.length}
                      </span>
                      <i
                        className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                          mobileServicesOpen ? 'rotate-180 text-sky-700' : 'text-slate-400'
                        }`}
                        aria-hidden="true"
                      />
                    </div>
                  </button>

                  {mobileServicesOpen && (
                    <div className="bg-white p-2 divide-y divide-slate-100 border-t border-slate-100">
                      {serviceLinks.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMobileMenuOpen(false);
                            setMobileServicesOpen(false);
                          }}
                          className={({ isActive }) =>
                            `flex items-center justify-between px-3.5 py-2.5 text-sm rounded-lg transition-all ${
                              isActive
                                ? 'bg-sky-50 text-[#0f2b48] font-bold border-l-4 border-[#c52828] shadow-2xs'
                                : 'text-slate-700 hover:text-[#0f2b48] hover:bg-slate-50 border-l-4 border-transparent'
                            }`
                          }
                        >
                          <span className="pr-2">{item.name}</span>
                          <i className="fa-solid fa-chevron-right text-xs opacity-50 shrink-0" aria-hidden="true" />
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>

                <NavLink
                  to="/kvalitet"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3.5 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                      isActive ? 'bg-[#0f2b48] text-white shadow-xs' : 'text-slate-800 hover:bg-slate-100'
                    }`
                  }
                >
                  Kvalitet
                </NavLink>

                <NavLink
                  to="/forsikring"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3.5 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                      isActive ? 'bg-[#0f2b48] text-white shadow-xs' : 'text-slate-800 hover:bg-slate-100'
                    }`
                  }
                >
                  Forsikring
                </NavLink>

                <NavLink
                  to="/spoergsmaal-svar-fragt"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3.5 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                      isActive ? 'bg-[#0f2b48] text-white shadow-xs' : 'text-slate-800 hover:bg-slate-100'
                    }`
                  }
                >
                  Ofte stillede spørgsmål
                </NavLink>

                <NavLink
                  to="/kendskabdownloads"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3.5 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                      isActive ? 'bg-[#0f2b48] text-white shadow-xs' : 'text-slate-800 hover:bg-slate-100'
                    }`
                  }
                >
                  Kendskab/Downloads
                </NavLink>

                <NavLink
                  to="/nyheder"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3.5 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                      isActive ? 'bg-[#0f2b48] text-white shadow-xs' : 'text-slate-800 hover:bg-slate-100'
                    }`
                  }
                >
                  Nyheder
                </NavLink>

                <NavLink
                  to="/om-os"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3.5 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                      isActive ? 'bg-[#0f2b48] text-white shadow-xs' : 'text-slate-800 hover:bg-slate-100'
                    }`
                  }
                >
                  Om os
                </NavLink>

                <NavLink
                  to="/kontakt"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3.5 py-2.5 text-base font-semibold rounded-lg transition-colors ${
                      isActive ? 'bg-[#0f2b48] text-white shadow-xs' : 'text-slate-800 hover:bg-slate-100'
                    }`
                  }
                >
                  Kontakt
                </NavLink>
              </div>

              {/* Mobile Contact & CTA Footer */}
              <div className="p-4 bg-slate-100 border-t border-slate-200 space-y-3 shrink-0">
                <Link
                  to="/fragtrater"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full flex items-center justify-center py-3 px-4 bg-[#b25d19] hover:bg-[#974d12] text-white font-semibold rounded-lg text-center transition-colors shadow-xs"
                >
                  <i className="fa-solid fa-file-invoice mr-2" aria-hidden="true" />
                  <span>Indsend Fragtforespørgsel</span>
                </Link>
                <div className="pt-2 text-xs text-slate-600 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-700">Telefon:</span>
                    <a href="tel:+4576121230" className="text-[#0f2b48] font-bold whitespace-nowrap">
                      +45 76 12 12 30
                    </a>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-700">Email:</span>
                    <a href="mailto:booking@fastlane.dk" className="text-sky-700 font-medium whitespace-nowrap">
                      booking@fastlane.dk
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
